import {
  Area,
  AreaRoomData,
  AreaConnectedUserCount,
  AreaUpdate,
  Peer,
  PrivateRoom,
  Site,
  SiteType,
  UserRole,
  DynamicContent
} from '@shared/models/';


export enum SocEvents {
  // Connection / generic events
  connection = 'connection',
  signal = 'signal',
  message = 'message',
  userNotFound = 'user-not-found',
  // User action related room events
  disconnect = 'disconnect',
  userReconnect = 'user-reconnect',
  userReconnectClosed = 'user-reconnect-closed',
  userJoined = 'user-joined',
  broadcastMessage = 'broadcast-message',
  userLeft = 'user-left',
  userRTCFailed = 'user-rtc-failed',
  updatePeerData = 'update-peer-data',
  userVideoStatusChanged = 'user-video-status-changed',
  userDoNotDisturbStatusChanged = 'user-do-not-disturb-status-changed',
  // Data related events
  dataArea = 'data-area',
  dataSites = 'data-sites',
  dataSiteTypes = 'data-site-types',
  dataDynamicContent = 'data-dynamic-content',
  joinArea = 'join-area',
  leaveArea = 'leave-area',
  areaUpdate = 'area-update',
  allLobbyData = 'all-lobby-data',
  drupalAction = 'drupal-trigger-action',
  dataAreaConnectedUserCount = 'data-area-connected-user-count',
  // Wave event
  wave = 'wave',
  // Report user event
  reportUser = 'report-user',
  userUnblocked = 'user-unblocked',
  streamUpdated = 'stream-updated',
  // Kick / boot event (blacklist)
  bootUser = 'boot-user',
  // User using multiple sessions to join rooms
  multiSession = 'multi-session-send',
  multiSessionAck = 'multi-session-ack',
  // Private room related events
  requestPrivateRoom = 'request-private-room',
  privateRoomReturned = 'private-room-returned',
  requestJoinRoom = 'private-room-join-request',
  joinPrivateRoom = 'join-private-room',
  privateRoomJoined = 'private-room-joined',
  leavePrivateRoom = 'private-room-leave',
  privateRoomNotFound = 'private-room-not-found',
  privateRoomLeft = 'private-room-left',
  // added for room joining
  areaRooms = 'area-rooms',
  // Administration / user role related events
  userRoles = 'user-roles',
  kickPeer = 'kick-peer',
  peerKicked = 'peer-kicked',
  thisPeerKicked = 'this-peer-kicked',
  adminMuteRoomUser = 'admin-mute-room-user', // @TODO: give this a better name
  muteRoomUser = 'mute-room-user',
  muteWholeRoom = 'mute-whole-room',
}

export interface ClientPacket {
  uuid: string;
  soc: string;
  displayName: string;
  videoStatus?: boolean;
  doNotDisturb?: boolean;
}

export interface UserReconnectPacket extends ClientPacket {
  id: string;
}

export interface UserRTCFailedPacket extends ClientPacket {
  failed_user: string;
}

export interface UserPacket {
  id: string;
  soc: string;
  displayName: string;
}

export interface LobbyData {
  readonly siteTypes: SiteType[];
  readonly sites: Site[];
  readonly areas: Area[];
  readonly dynamicContent: DynamicContent[];
  hasPerformerStreams: boolean;
  hasAudioStreams: boolean;
}

export interface RoomPeer extends Peer {
  roomId?: string;
}

export interface ListenEvents {
  [SocEvents.signal]: (uuid: string, message: string) => void;
  [SocEvents.message]: (uuid: string) => void;
  [SocEvents.leaveArea]: (callback: Function) => void;
  [SocEvents.userRTCFailed]: (uuid: string) => void;
  [SocEvents.userReconnect]: (uuid: string) => void;
  [SocEvents.userReconnectClosed]: (uuid: string) => void;
  [SocEvents.wave]: (uuid: string) => void;
  [SocEvents.reportUser]: (uuid: string) => void;
  [SocEvents.userUnblocked]: (uuid: string) => void;
  [SocEvents.userNotFound]: () => void;
  [SocEvents.bootUser]: () => void;
  [SocEvents.streamUpdated]: (uuid: string) => void;
  [SocEvents.userVideoStatusChanged]: (uuid: string, status: boolean) => void;
  [SocEvents.userDoNotDisturbStatusChanged]: (uuid: string, status: boolean) => void;
  [SocEvents.multiSession]: (socketId: string, callback: Function) => void;
  // Private rooms
  [SocEvents.requestPrivateRoom]: (userUuid: string, areaUuid: string) => void;
  [SocEvents.requestJoinRoom]: (joinId: string, userUuid: string, areaUuid: string) => void;
  [SocEvents.leavePrivateRoom]: (room: PrivateRoom) => void;
  [SocEvents.joinPrivateRoom]: (room: PrivateRoom) => void;
  // Admin / user role related events
  [SocEvents.kickPeer]: (peerUuid: string) => void;
  [SocEvents.peerKicked]: (peerUuid: string) => void;
  [SocEvents.thisPeerKicked]: () => void;
  [SocEvents.adminMuteRoomUser]: (uuid: string, status: boolean) => void;
}
export interface EmitEvents {
  [SocEvents.broadcastMessage]: (id: string, data: string) => void;
  [SocEvents.signal]: (message: string) => void;
  [SocEvents.userLeft]: (uuid: string) => void;
  [SocEvents.userJoined]: (client: ClientPacket) => void;
  [SocEvents.userRTCFailed]: (client: UserRTCFailedPacket) => void;
  [SocEvents.userReconnect]: (client: ClientPacket) => void;
  [SocEvents.userReconnectClosed]: (client: UserReconnectPacket) => void;
  [SocEvents.wave]: (client: UserPacket) => void;
  [SocEvents.reportUser]: (client: UserPacket) => void;
  [SocEvents.userUnblocked]: (client: UserPacket) => void;
  [SocEvents.userNotFound]: () => void;
  [SocEvents.bootUser]: () => void;
  [SocEvents.streamUpdated]: (client: UserPacket) => void;
  [SocEvents.allLobbyData]: (data: LobbyData) => void;
  [SocEvents.userVideoStatusChanged]: (client: {uuid: string, status: boolean}) => void;
  [SocEvents.userDoNotDisturbStatusChanged]: (client: {uuid: string, status: boolean}) => void;
  // Area events
  [SocEvents.dataSiteTypes]: (siteTypes: SiteType[]) => void;
  [SocEvents.dataSites]: (sites: Site[]) => void;
  [SocEvents.dataArea]: (areas: Area[]) => void;
  [SocEvents.dataDynamicContent]: (dynamicContent: DynamicContent[]) => void;
  [SocEvents.updatePeerData]: (peers: RoomPeer[]) => void;
  [SocEvents.areaUpdate]: (areas: AreaUpdate[]) => void;
  [SocEvents.dataAreaConnectedUserCount]: (updates: AreaConnectedUserCount[]) => void;
  [SocEvents.areaRooms]: (areaRooms: AreaRoomData[]) => void;
  // Private rooms
  [SocEvents.privateRoomNotFound]: () => void;
  [SocEvents.privateRoomReturned]: (room: PrivateRoom) => void;
  [SocEvents.privateRoomLeft]: () => void;
  [SocEvents.privateRoomJoined]: () => void;
  // Administration / user role events
  [SocEvents.userRoles]: (user_roles: UserRole[]) => void;
  [SocEvents.kickPeer]: (peerUuid: string) => void;
  [SocEvents.peerKicked]: (peerUuid: string) => void;
  [SocEvents.thisPeerKicked]: () => void;
  [SocEvents.muteRoomUser]: (status: boolean) => void;
}
export interface ServerEvents {
  // TODO fill me?
}
export interface SocketData {
  // TODO fill me?
}
